import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import Swal from 'sweetalert2'
import moment from 'moment'
import { editBadges, viewBadgeDetails } from '../../slice/badgeSlice'

// Validation Schema
const badgeSchema = yup.object().shape({
  name: yup.string().required('Badge name is required').min(3).max(100),

  description: yup
    .string()
    .required('Description is required')
    .min(10)
    .max(300),

  min_completed_bookings: yup
    .number()
    .required('Minimum completed bookings required')
    .min(0)
    .typeError('Must be a number'),

  min_avg_rating: yup
    .number()
    .required('Minimum rating required')
    .min(0)
    .max(5)
    .typeError('Must be a number'),

  min_hours_per_month: yup
    .number()
    .required('Minimum hours required')
    .min(0)
    .typeError('Must be a number')
})

const EditBadge = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const badgeId = location.state?.badgeId
  const { badgeDetail, loading } = useSelector(state => state.badge)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [preview, setPreview] = useState(null)
  const [imageFile, setImageFile] = useState(null)

  const DEFAULT_BADGE = process.env.PUBLIC_URL + '/assets/images/default-badge.png'

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm({
    resolver: yupResolver(badgeSchema),
    defaultValues: {
      name: '',
      description: '',
      min_completed_bookings: '',
      min_avg_rating: '',
      min_hours_per_month: ''
    }
  })

  // Fetch badge details
  useEffect(() => {
    if (!badgeId) {
      navigate('/badges')
      return
    }
    dispatch(viewBadgeDetails({ id: badgeId }))
  }, [badgeId, dispatch, navigate])

  // Populate form
  useEffect(() => {
    if (badgeDetail) {
      setValue('name', badgeDetail.name || '')
      setValue('description', badgeDetail.description || '')
      setValue('min_completed_bookings', badgeDetail.min_completed_bookings || '')
      setValue('min_avg_rating', badgeDetail.min_avg_rating || '')
      setValue('min_hours_per_month', badgeDetail.min_hours_per_month || '')

      if (badgeDetail.image) {
        setPreview(`${process.env.REACT_APP_IMAGE_URL}/${badgeDetail.image}`)
      } else {
        setPreview(DEFAULT_BADGE)
      }
    }
  }, [badgeDetail, setValue])

  // Image change
  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setImageFile(file)
      setPreview(URL.createObjectURL(file))
    }
  }

  const onSubmit = async (data) => {
    setIsSubmitting(true)

    try {
      const formData = new FormData()
      formData.append('id', badgeId)
      formData.append('name', data.name)
      formData.append('description', data.description)
      formData.append('min_completed_bookings', data.min_completed_bookings)
      formData.append('min_avg_rating', data.min_avg_rating)
      formData.append('min_hours_per_month', data.min_hours_per_month)

      // Only send image if changed
      if (imageFile) {
        formData.append('image', imageFile)
      }

      const response = await dispatch(editBadges(formData))

      if (response?.payload?.code === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Badge updated successfully',
          timer: 2000,
          showConfirmButton: false
        })

        setTimeout(() => navigate('/badges'), 2000)
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response?.payload?.message || 'Failed to update badge'
        })
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Something went wrong'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary"></div>
      </div>
    )
  }

  // No data
  if (!badgeDetail) {
    return (
      <div className="text-center py-5">
        <h5>No badge found</h5>
        <button className="btn btn-primary mt-3" onClick={() => navigate('/badges')}>
          Back to Badges
        </button>
      </div>
    )
  }

  return (
    <div className="container-fluid px-4 py-3">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-semibold mb-0">Edit Badge</h4>
        <button className="btn btn-outline-secondary" onClick={() => navigate('/badges')}>
          ← Back to List
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-light fw-semibold">
            Badge Information
          </div>

          <div className="card-body p-4">
            <div className="row g-3">

              {/* Name */}
              <div className="col-md-6">
                <label className="form-label">Badge Name *</label>
                <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} {...register('name')} />
                {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
              </div>

              {/* Image */}
              <div className="col-md-6">
                <label className="form-label">Badge Image</label>
                <input type="file" className="form-control" accept="image/*" onChange={handleImageChange} />
              </div>

              {/* Preview */}
              {preview && (
                <div className="col-12">
                  <label className="form-label d-block">Preview</label>
                  <img
                    src={preview || DEFAULT_BADGE}
                    alt="badge"
                    onError={(e) => {
                      e.currentTarget.onerror = null
                      e.currentTarget.src = DEFAULT_BADGE
                    }}
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '8px',
                      objectFit: 'contain',
                      border: '1px solid #dee2e6',
                      padding: '6px',
                      backgroundColor: '#fff'
                    }}
                  />
                </div>
              )}

              {/* Description */}
              <div className="col-12">
                <label className="form-label">Description *</label>
                <textarea
                  className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                  rows="3"
                  {...register('description')}
                />
              </div>

              {/* Conditions */}
              <div className="col-md-4">
                <label className="form-label">Min Completed Bookings *</label>
                <input type="number" className="form-control" {...register('min_completed_bookings')} />
              </div>

              <div className="col-md-4">
                <label className="form-label">Min Avg Rating *</label>
                <input type="number" step="0.1" className="form-control" {...register('min_avg_rating')} />
              </div>

              <div className="col-md-4">
                <label className="form-label">Min Hours / Month *</label>
                <input type="number" className="form-control" {...register('min_hours_per_month')} />
              </div>

              {/* Info */}
              <div className="col-12 mt-3">
                <small className="text-muted">
                  Created: {badgeDetail?.createdAt ? moment(badgeDetail.createdAt).format('DD MMM YYYY, hh:mm A') : 'NA'}
                </small>
                <br />
                <small className="text-muted">
                  Updated: {badgeDetail?.updatedAt ? moment(badgeDetail.updatedAt).format('DD MMM YYYY, hh:mm A') : 'NA'}
                </small>
              </div>

            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="d-flex gap-3 justify-content-end">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/badges')}>
            Cancel
          </button>

          <button type="submit" className="btn btn-success" disabled={isSubmitting}>
            {isSubmitting ? 'Updating...' : 'Update Badge'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default EditBadge